import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { FaUser, FaCheckCircle, FaTimes, FaCrown, FaHistory } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

export default function AdminUsers() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { API, user } = useContext(AppContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [showMultiRoleModal, setShowMultiRoleModal] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users:', error);
      setLoading(false);
    }
  };


  useEffect(() => {
    if (user?.role === 'admin') {
      fetchUsers();
    }
  }, [user]);

  const handleChangeRole = (userId, currentRole) => {
    const userToChange = users.find(u => u.id === userId);
    setSelectedUser({ ...userToChange, currentRole });
    setNewRole('');
    setShowRoleModal(true);
  };

  const handleManageRoles = (userId) => {
    const userToChange = users.find(u => u.id === userId);
    setSelectedUser(userToChange);
    setSelectedRoles(userToChange.roles || [userToChange.role]);
    setShowMultiRoleModal(true);
  };

  const toggleRole = (role) => {
    if (selectedRoles.includes(role)) {
      // Ne pas permettre de décocher si c'est le seul rôle
      if (selectedRoles.length === 1) {
        toast.error(i18n.language === 'fr' ? 'Au moins un rôle doit être assigné' : 'At least one role must be assigned');
        return;
      }
      setSelectedRoles(selectedRoles.filter(r => r !== role));
    } else {
      setSelectedRoles([...selectedRoles, role]);
    }
  };

  const submitRolesChange = async () => {
    if (selectedRoles.length === 0) {
      toast.error(i18n.language === 'fr' ? 'Au moins un rôle doit être assigné' : 'At least one role must be assigned');
      return;
    }

    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}/roles`, {
        roles: selectedRoles,
        admin_id: user.id
      });
      toast.success(i18n.language === 'fr' ? 'Rôles mis à jour avec succès' : 'Roles updated successfully');
      setShowMultiRoleModal(false);
      setSelectedUser(null);
      setSelectedRoles([]);
      fetchUsers();
    } catch (error) {
      console.error('Error updating roles:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors de la mise à jour des rôles' : 'Error updating roles');
    }
  };

  const submitRoleChange = async () => {
    if (!newRole) {
      toast.error(i18n.language === 'fr' ? 'Veuillez sélectionner un rôle' : 'Please select a role');
      return;
    }

    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}/change-role?admin_id=${user.id}&new_role=${newRole}`);
      toast.success(i18n.language === 'fr' ? 'Rôle modifié avec succès' : 'Role changed successfully');
      setShowRoleModal(false);
      setSelectedUser(null);
      setNewRole('');
      fetchUsers();
    } catch (error) {
      console.error('Error changing role:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors du changement de rôle' : 'Error changing role');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (userId === user.id) {
      toast.error(i18n.language === 'fr' ? 'Vous ne pouvez pas vous supprimer vous-même' : 'Cannot delete your own account');
      return;
    }

    if (!window.confirm(i18n.language === 'fr' ? 'Êtes-vous sûr de vouloir supprimer cet utilisateur ?' : 'Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/users/${userId}?admin_id=${user.id}`);
      toast.success(i18n.language === 'fr' ? 'Utilisateur supprimé' : 'User deleted');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors de la suppression' : 'Error deleting user');
    }
  };

  const toggleReviewPermission = async (userId, currentPermission) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/review-permission?can_review=${!currentPermission}`);
      toast.success(
        !currentPermission 
          ? (i18n.language === 'fr' ? 'Permission d\'avis activée' : 'Review permission enabled')
          : (i18n.language === 'fr' ? 'Permission d\'avis désactivée' : 'Review permission disabled')
      );
      fetchUsers();
    } catch (error) {
      console.error('Error toggling review permission:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors de la modification de la permission' : 'Error changing permission');
    }
  };



  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-600">{t('loading')}</p>
        </div>
      </AdminLayout>
    );
  }



  const adminUsers = users.filter(u => u.roles?.includes('admin') || u.role === 'admin').length;
  const grandCompteUsers = users.filter(u => u.roles?.includes('grand_compte')).length;
  const supplierUsers = users.filter(u => u.roles?.includes('supplier') || u.role === 'supplier').length;
  const regularUsers = users.filter(u => {
    const roles = u.roles || [u.role];
    return roles.includes('user') && !roles.includes('admin') && !roles.includes('supplier') && !roles.includes('grand_compte');
  }).length;

  return (
    <AdminLayout>
      <div className="min-h-screen bg-[#F5F5F5]">
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-4xl font-bold text-[#0A2540] mb-8">
            {i18n.language === 'fr' ? 'Gestion des Utilisateurs' : 'User Management'}
          </h1>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">
                      {i18n.language === 'fr' ? 'Total Utilisateurs' : 'Total Users'}
                    </p>
                    <p className="text-3xl font-bold text-[#0A2540]">{users.length}</p>
                  </div>
                  <FaUser className="text-4xl text-gray-300" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">
                      {i18n.language === 'fr' ? 'Administrateurs' : 'Admins'}
                    </p>
                    <p className="text-3xl font-bold text-purple-600">{adminUsers}</p>
                  </div>
                  <FaCrown className="text-4xl text-purple-300" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">
                      {i18n.language === 'fr' ? 'Grands Comptes' : 'Enterprise'}
                    </p>
                    <p className="text-3xl font-bold text-orange-600">{grandCompteUsers}</p>
                  </div>
                  <FaCheckCircle className="text-4xl text-orange-300" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">
                      {i18n.language === 'fr' ? 'Fournisseurs' : 'Suppliers'}
                    </p>
                    <p className="text-3xl font-bold text-[#635BFF]">{supplierUsers}</p>
                  </div>
                  <FaCheckCircle className="text-4xl text-[#635BFF]" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">
                      {i18n.language === 'fr' ? 'Utilisateurs' : 'Users'}
                    </p>
                    <p className="text-3xl font-bold text-blue-600">{regularUsers}</p>
                  </div>
                  <FaUser className="text-4xl text-blue-300" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Mobile Scroll Hint */}
          <div className="md:hidden mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-700">
              ℹ️ {i18n.language === 'fr' ? 'Faites défiler le tableau vers la droite → pour voir toutes les colonnes' : 'Scroll the table to the right → to see all columns'}
            </p>
          </div>

          {/* Users Table */}
          <Card>
            <CardContent className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full min-w-max" style={{minWidth: '800px'}}>
                  <thead className="border-b-2">
                    <tr className="text-left">
                      <th className="pb-3 font-semibold whitespace-nowrap px-2">
                        {i18n.language === 'fr' ? 'Utilisateur' : 'User'}
                      </th>
                      <th className="pb-3 font-semibold whitespace-nowrap px-2">Email</th>
                      <th className="pb-3 font-semibold whitespace-nowrap px-2">Rôle</th>
                      <th className="pb-3 font-semibold whitespace-nowrap px-2">
                        {i18n.language === 'fr' ? 'Peut soumettre avis' : 'Can Review'}
                      </th>
                      <th className="pb-3 font-semibold whitespace-nowrap px-2">
                        {i18n.language === 'fr' ? 'Inscription' : 'Joined'}
                      </th>
                      <th className="pb-3 font-semibold whitespace-nowrap px-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b hover:bg-gray-50">
                        <td className="py-4 px-2">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#0A2540] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                              {u.name?.charAt(0).toUpperCase()}
                            </div>
                            <span className="font-medium whitespace-nowrap">{u.name}</span>
                          </div>
                        </td>
                        <td className="py-4 text-gray-600 px-2 whitespace-nowrap">{u.email}</td>
                        <td className="py-4 px-2">
                          <div className="flex flex-wrap gap-1">
                            {(u.roles || [u.role]).map((role, idx) => (
                              <span key={idx} className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap inline-block ${
                                role === 'admin' 
                                  ? 'bg-purple-100 text-purple-700' 
                                  : role === 'grand_compte'
                                  ? 'bg-[#00D4FF]/10 text-orange-700'
                                  : role === 'supplier'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                {role === 'admin' ? '👑 Admin' : role === 'grand_compte' ? '🏢 Grand Compte' : role === 'supplier' ? '🏭 Fournisseur' : '👤 User'}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="py-4 px-2">
                          <button
                            onClick={() => toggleReviewPermission(u.id, u.can_review)}
                            className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                              u.can_review
                                ? 'bg-[#635BFF]/10 text-[#5248d9]'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {u.can_review ? '✓ Autorisé' : '✗ Non autorisé'}
                          </button>
                        </td>
                        <td className="py-4 text-gray-600 text-sm px-2 whitespace-nowrap">
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-4 px-2">
                          <div className="flex gap-2 flex-wrap">
                            <button
                              onClick={() => handleManageRoles(u.id)}
                              className="bg-[#635BFF] text-white px-3 py-2 rounded hover:bg-[#5248d9] text-sm whitespace-nowrap font-semibold"
                            >
                              {i18n.language === 'fr' ? 'Gérer rôles' : 'Manage Roles'}
                            </button>
                            <button
                              onClick={() => navigate(`/admin/users/${u.id}/history`)}
                              className="bg-[#0A2540] text-white px-3 py-2 rounded hover:bg-[#003a52] text-sm whitespace-nowrap font-semibold flex items-center gap-2"
                            >
                              <FaHistory />
                              {i18n.language === 'fr' ? 'Historique' : 'History'}
                            </button>
                            {!(u.roles || [u.role]).includes('admin') && (
                              <button
                                onClick={() => handleDeleteUser(u.id)}
                                className="bg-red-600 text-white px-3 py-2 rounded hover:bg-red-700 text-sm whitespace-nowrap font-semibold"
                              >
                                {i18n.language === 'fr' ? 'Supprimer' : 'Delete'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Role Change Modal */}
      {showRoleModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-[#0A2540] mb-4">
              {i18n.language === 'fr' ? 'Changer le rôle' : 'Change Role'}
            </h2>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                {i18n.language === 'fr' ? 'Utilisateur:' : 'User:'} <strong>{selectedUser.name}</strong>
              </p>
              <p className="text-sm text-gray-600 mb-4">
                {i18n.language === 'fr' ? 'Rôle actuel:' : 'Current role:'} <strong>{selectedUser.currentRole}</strong>
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold mb-3">
                {i18n.language === 'fr' ? 'Nouveau rôle:' : 'New role:'}
              </label>
              
              <div className="space-y-3">
                <button
                  onClick={() => setNewRole('user')}
                  disabled={selectedUser.currentRole === 'user'}
                  className={`w-full p-4 rounded-lg border-2 text-left transition ${
                    newRole === 'user'
                      ? 'border-[#635BFF] bg-[#635BFF]/5'
                      : selectedUser.currentRole === 'user'
                      ? 'border-gray-200 bg-gray-100 cursor-not-allowed'
                      : 'border-gray-200 hover:border-[#635BFF]'
                  }`}
                >
                  <div className="font-semibold">
                    👤 {i18n.language === 'fr' ? 'Utilisateur' : 'User'}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {i18n.language === 'fr' ? 'Accès utilisateur standard' : 'Standard user access'}
                  </div>
                </button>

                <button
                  onClick={() => setNewRole('supplier')}
                  disabled={selectedUser.currentRole === 'supplier'}
                  className={`w-full p-4 rounded-lg border-2 text-left transition ${
                    newRole === 'supplier'
                      ? 'border-[#635BFF] bg-[#635BFF]/5'
                      : selectedUser.currentRole === 'supplier'
                      ? 'border-gray-200 bg-gray-100 cursor-not-allowed'
                      : 'border-gray-200 hover:border-[#635BFF]'
                  }`}
                >
                  <div className="font-semibold">
                    🏭 {i18n.language === 'fr' ? 'Fournisseur' : 'Supplier'}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {i18n.language === 'fr' ? 'Peut voir opportunités et soumettre devis' : 'Can view opportunities and submit quotes'}
                  </div>
                </button>

                <button
                  onClick={() => setNewRole('admin')}
                  disabled={selectedUser.currentRole === 'admin'}
                  className={`w-full p-4 rounded-lg border-2 text-left transition ${
                    newRole === 'admin'
                      ? 'border-[#635BFF] bg-[#635BFF]/5'
                      : selectedUser.currentRole === 'admin'
                      ? 'border-gray-200 bg-gray-100 cursor-not-allowed'
                      : 'border-gray-200 hover:border-[#635BFF]'
                  }`}
                >
                  <div className="font-semibold">
                    👑 Admin
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    {i18n.language === 'fr' ? 'Accès complet à l\'administration' : 'Full admin access'}
                  </div>
                </button>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={submitRoleChange}
                disabled={!newRole}
                className="flex-1 bg-[#635BFF] text-white px-6 py-3 rounded-lg hover:bg-[#5248d9] font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {i18n.language === 'fr' ? '✓ Confirmer' : '✓ Confirm'}
              </button>
              <button
                onClick={() => {
                  setShowRoleModal(false);
                  setSelectedUser(null);
                  setNewRole('');
                }}
                className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Multi-Role Management Modal */}
      {showMultiRoleModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-[#0A2540] mb-4">
              {i18n.language === 'fr' ? 'Gérer les rôles' : 'Manage Roles'}
            </h2>
            <p className="text-gray-600 mb-4">
              {i18n.language === 'fr' 
                ? `Sélectionnez les rôles pour ${selectedUser.name}` 
                : `Select roles for ${selectedUser.name}`}
            </p>

            <div className="space-y-3 mb-6">
              {/* User Role */}
              <button
                onClick={() => toggleRole('user')}
                className={`w-full p-4 rounded-lg border-2 text-left transition ${
                  selectedRoles.includes('user')
                    ? 'border-gray-500 bg-gray-50'
                    : 'border-gray-200 hover:border-gray-400'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">
                      👤 {i18n.language === 'fr' ? 'Utilisateur' : 'User'}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {i18n.language === 'fr' ? 'Accès client standard' : 'Standard client access'}
                    </div>
                  </div>
                  {selectedRoles.includes('user') && (
                    <span className="text-[#635BFF] text-2xl">✓</span>
                  )}
                </div>
              </button>

              {/* Grand Compte Role */}
              <button
                onClick={() => toggleRole('grand_compte')}
                className={`w-full p-4 rounded-lg border-2 text-left transition ${
                  selectedRoles.includes('grand_compte')
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-200 hover:border-orange-400'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">
                      🏢 {i18n.language === 'fr' ? 'Grand Compte' : 'Enterprise Account'}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {i18n.language === 'fr' 
                        ? 'Accès projets/chantiers avec gestion RFQ automatisée' 
                        : 'Access to projects with automated RFQ management'}
                    </div>
                  </div>
                  {selectedRoles.includes('grand_compte') && (
                    <span className="text-orange-600 text-2xl">✓</span>
                  )}
                </div>
              </button>

              {/* Supplier Role */}
              <button
                onClick={() => toggleRole('supplier')}
                className={`w-full p-4 rounded-lg border-2 text-left transition ${
                  selectedRoles.includes('supplier')
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-400'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">
                      🏭 {i18n.language === 'fr' ? 'Fournisseur' : 'Supplier'}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {i18n.language === 'fr' 
                        ? 'Peut gérer produits, devis et opportunités' 
                        : 'Can manage products, quotes and opportunities'}
                    </div>
                  </div>
                  {selectedRoles.includes('supplier') && (
                    <span className="text-blue-600 text-2xl">✓</span>
                  )}
                </div>
              </button>

              {/* Admin Role */}
              <button
                onClick={() => toggleRole('admin')}
                className={`w-full p-4 rounded-lg border-2 text-left transition ${
                  selectedRoles.includes('admin')
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-400'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">
                      👑 {i18n.language === 'fr' ? 'Administrateur' : 'Administrator'}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {i18n.language === 'fr' ? 'Accès complet au panel admin' : 'Full admin panel access'}
                    </div>
                  </div>
                  {selectedRoles.includes('admin') && (
                    <span className="text-purple-600 text-2xl">✓</span>
                  )}
                </div>
              </button>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
              <p className="text-sm text-blue-800">
                💡 {i18n.language === 'fr' 
                  ? 'Un utilisateur peut avoir plusieurs rôles simultanément. Par exemple, un admin peut aussi être fournisseur.' 
                  : 'A user can have multiple roles simultaneously. For example, an admin can also be a supplier.'}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={submitRolesChange}
                disabled={selectedRoles.length === 0}
                className={`flex-1 py-3 rounded-lg font-semibold ${
                  selectedRoles.length === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-[#635BFF] text-white hover:bg-[#5248d9]'
                }`}
              >
                {i18n.language === 'fr' ? '✓ Enregistrer' : '✓ Save'}
              </button>
              <button
                onClick={() => {
                  setShowMultiRoleModal(false);
                  setSelectedUser(null);
                  setSelectedRoles([]);
                }}
                className="flex-1 py-3 border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
